// ─────────────────────────────────────────────────────────────────────────────
// api.js — Central API client
// Location: src/services/api.js
//
// Token management:
//   Cognito ID tokens are stored in JS module memory only (_token variable).
//   Amplify handles session persistence across page refreshes internally --
//   on bootstrap, AppContext calls fetchAuthSession() to get a fresh token
//   and sets it here via setToken(). sessionStorage is not used.
//
// All backend communication goes through request() which attaches the token
//   automatically. Callers never touch headers or fetch directly.
// ─────────────────────────────────────────────────────────────────────────────

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Module-level token — lives in JS memory, never written to DOM storage.
// Set by AppContext after Cognito login; cleared on logout.
let _token = null;

export function setToken(t)  { _token = t; }
export function getToken()   { return _token; }
export function clearToken() { _token = null; }

// ── Core fetch wrapper ────────────────────────────────────────────────────────

async function request(method, path, body = null, isForm = false) {
  const headers = {};
  if (_token)           headers['Authorization'] = `Bearer ${_token}`;
  if (body && !isForm)  headers['Content-Type']  = 'application/json';

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body ? (isForm ? body : JSON.stringify(body)) : undefined,
  });

  if (res.status === 204) return null;
  const text = await res.text();
  const data = text ? JSON.parse(text) : {};

  if (!res.ok) {
    const msg = data.error
      || (data.errors && data.errors.map(e => e.msg).join(', '))
      || `Request failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    err.data   = data;
    throw err;
  }
  return data;
}

const get    = (path)       => request('GET',    path);
const post   = (path, body) => request('POST',   path, body);
const patch  = (path, body) => request('PATCH',  path, body);
const del    = (path)       => request('DELETE', path);
const upload = (path, fd)   => request('POST',   path, fd, true);

// ── Auth ──────────────────────────────────────────────────────────────────────
// Note: login and changePassword are now handled by Amplify in AppContext.
// authAPI.me() is still used to fetch the MongoDB user after Cognito login.
// authAPI.changePassword is kept for non-first-login password changes if needed.

export const authAPI = {
  me:             ()                             => get('/auth/me'),
  changePassword: (newPassword) =>
  post('/auth/change-password', {
    newPassword,
  }),
};

// ── Hospitals (SuperAdmin only) ───────────────────────────────────────────────

export const hospitalsAPI = {
  list:        ()         => get('/hospitals'),
  get:         (id)       => get(`/hospitals/${id}`),
  create:      (data)     => post('/hospitals', data),
  update:      (id, data) => patch(`/hospitals/${id}`, data),
  updateAdmin: (id, data) => patch(`/hospitals/${id}/admin`, data),
};

// ── Users ─────────────────────────────────────────────────────────────────────

export const usersAPI = {
  listDoctors:      (hospitalID) => get(`/users/doctors${hospitalID ? `?hospitalID=${hospitalID}` : ''}`),
  createDoctor:     (data)       => post('/users/doctors', data),
  updateDoctor:     (id, data)   => patch(`/users/doctors/${id}`, data),
  updateMe:         (data)       => patch('/users/me', data),
  deactivateDoctor: (id)         => del(`/users/doctors/${id}`),
  updateAdmin:      (hospitalId, data) => patch(`/users/admin/${hospitalId}`, data),
};

// ── Patients ──────────────────────────────────────────────────────────────────

export const patientsAPI = {
  list:    (all = false) => get(`/patients${all ? '?all=true' : ''}`),
  get:     (id)          => get(`/patients/${id}`),
  create:  (data)        => post('/patients', data),
  update:  (id, data)    => patch(`/patients/${id}`, data),
  addNote: (id, text)    => post(`/patients/${id}/notes`, { text }),
};

// ── Sessions ──────────────────────────────────────────────────────────────────

export const sessionsAPI = {
  listForPatient: (patientId) => get(`/sessions/patient/${patientId}`),

  // Submit a recording session — 4 WAV blobs as multipart/form-data.
  // Accepts both calling conventions:
  //   Recording.js:  tracks = [{ taskSuffix, blob, fileName }]
  //   Old draft:     tracks = [{ task: { suffix }, blob }]
  create: (patientId, tracks, { recordingDate, doctorNote } = {}) => {
    const fd = new FormData();
    fd.append('patientId', patientId);
    if (recordingDate) fd.append('recordingDate', recordingDate);
    if (doctorNote)    fd.append('doctorNote',    doctorNote);
    tracks.forEach((t) => {
      // Support both { taskSuffix, blob, fileName } and { task: { suffix }, blob }
      const suffix   = t.taskSuffix ?? t.task?.suffix;
      const blob     = t.blob;
      const fileName = t.fileName ?? `${suffix}.wav`;
      fd.append(suffix, blob, fileName);
    });
    return upload('/sessions', fd);
  },

  // SuperAdmin data export — returns flat rows for xlsx conversion
  export: ({ hospitalIds = [], dateFrom = '', dateTo = '' } = {}) => {
    const params = new URLSearchParams();
    if (hospitalIds.length) params.set('hospitalIds', hospitalIds.join(','));
    if (dateFrom)           params.set('dateFrom',    dateFrom);
    if (dateTo)             params.set('dateTo',      dateTo);
    return get(`/sessions/export?${params.toString()}`);
  },

  // Returns { reportUrl } — fresh presigned URL for the session PDF.
  // Generates and uploads PDF on-demand if it doesn't exist yet.
  getReport: (sessionId) => get(`/sessions/${sessionId}/report`),
};
